import { BaseMeta, BaseModel } from '../base.model';
import { JsonObject, UiOption } from '../../helpers';
import { ChipStatus } from '@/components/chip/status-chip';
import { EvaluatedLevel } from '../user-event';
import { IconType } from '@/components/icon/helpers';
import { SurePathServiceName } from '../user-event/helpers';
import { PsFeatureMatrix, PsFeatureOpts, PsSupportConfig } from './support';
import { PsRisk, PsSignalSet, SignalOpts, PsTag, PromptFeatureOpts } from '.';

export type PublicServiceMeta = BaseMeta & {
  name: string;
  image: string;
  description: string;
  supportLevels: string[];
  risk: PsRisk;
  riskLevel: EvaluatedLevel;
  isF5WAIS: boolean;
  status: ChipStatus;
  isDiscovered: boolean;
};

export class PublicServiceModel extends BaseModel {
  type: string;
  name: string;
  description: string;
  image: string;
  hosts: string[];
  unauthenticatedHosts: string[];
  website: string;
  companyName: {
    value: string;
    excerpts: string[];
  };
  support: PsSupportConfig;
  signals: PsSignalSet;
  risk: PsRisk;
  tags: PsTag[];

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof PublicServiceModel, unknown>;

    this.type = (data.type as string) || '';
    this.name = (data.name as string) || '';
    this.description = (data.description as string) || '';
    this.image = (data.image as string) || '';
    this.hosts = (data.hosts as string[]) || [];
    this.unauthenticatedHosts = (data.unauthenticatedHosts as string[]) || [];
    this.website = (data.website as string) || '';
    this.companyName = (data.companyName as { value: string; excerpts: string[] }) || {
      value: '',
      excerpts: [],
    };

    this.support = new PsSupportConfig(data.support as JsonObject);

    this.signals = (data.signals as PsSignalSet) || {
      companyCountry: {
        value: '',
        excerpts: [],
        description: '',
      },
      trainOnUserData: {
        value: '',
        excerpts: [],
        description: '',
      },
      noModeration: {
        value: '',
        excerpts: [],
        description: '',
      },
      confidence: 0,
    };

    this.risk = (data.risk as PsRisk) || {
      base: { level: 'LOW', score: 0 },
      weighted: { level: 'LOW', score: 0 },
    };

    this.tags = (data.tags as PsTag[]) || [];
  }

  get activeSignalLabels(): string[] {
    const labels: string[] = [];

    if (this.signals.noModeration?.value) {
      labels.push(
        (SignalOpts.find(({ value }) => value === 'noModeration')?.label as string) || ''
      );
    }

    if (this.signals.trainOnUserData?.value) {
      labels.push(
        (SignalOpts.find(({ value }) => value === 'trainOnUserData')?.label as string) || ''
      );
    }

    return labels;
  }

  get riskLevel(): EvaluatedLevel {
    return this.risk.base.level.toLowerCase() as EvaluatedLevel;
  }

  get confidenceOption(): { label: string; icon: IconType } {
    if (this.signals.confidence === 1) {
      return { label: 'MEDIUM', icon: 'check' };
    }

    return this.signals.confidence > 1
      ? { label: 'HIGH', icon: 'arrow-double-up' }
      : { label: 'LOW', icon: 'arrow-double-down' };
  }

  get interceptScore(): number {
    return Object.keys(this.support.featureMatrix).filter(
      (key) => this.support.featureMatrix[key as keyof PsFeatureMatrix].enabled
    ).length;
  }

  get interceptFeatures(): UiOption[] {
    return PsFeatureOpts.filter(
      ({ value }) => this.support.featureMatrix[value as keyof PsFeatureMatrix].enabled
    );
  }

  /*
   * Request
   * sensitive-data == PII Entities, Programming Language - Content Controls, Custom Regex Classifiers
   * confidential-data == Confidential Data - Content Controls (technically its dsd service, but separate tag)
   * prompt-guard-input == Prompt Injection - Content Controls
   * confidential-datasources == Confidential Datasources
   * intent == Intent Controls (system and custom), Harmful Content (aka toxicity, used to be prompt-guard-input/output, but now its intent service embeddings)
   * high risk == always on, uses all available context, bare minimum is service risk score
   * context - turns rag on/off for the portal
   *
   * Response
   * prompt-guard-output == Bias (baked into risk score, not used for control)
   * - whether or not response is stored in user event
   */
  get promptGuards(): UiOption[] {
    const enabledOpts: string[] = [];

    if (this.tags.includes('sensitive-data')) {
      enabledOpts.push('pii-entities');
      enabledOpts.push('programming-language');
      enabledOpts.push('regex-classifiers');
    }

    if (this.tags.includes('confidential-data')) {
      enabledOpts.push('confidential-data');
    }

    if (this.tags.includes('prompt-guard-input')) {
      enabledOpts.push('prompt-injection');
    }

    if (this.tags.includes('intent')) {
      enabledOpts.push('intent');
      enabledOpts.push('harmful-content');
    }

    return PromptFeatureOpts.filter(({ value }) => enabledOpts.includes(value));
  }

  get promptGuardStatus(): 'enabled' | 'disabled' | 'partial' {
    if (this.promptGuards.length === PromptFeatureOpts.length) {
      return 'enabled';
    }

    if (this.promptGuards.length) {
      return 'partial';
    }

    return 'disabled';
  }

  get supportLevels(): string[] {
    if (this.support.level === 'access') {
      return ['Access control'];
    }

    if (this.support.level === 'request-intercept') {
      return ['Request intercept'];
    }

    if (this.support.level === 'intercept') {
      return ['Request intercept', 'Response intercept'];
    }

    return [];
  }

  get isDeprecated(): boolean {
    return !!this.support.deprecated?.value;
  }

  get status(): ChipStatus {
    if (this.isDeprecated) {
      return 'deprecated';
    }

    const { serviceState } = this.support;

    if (
      serviceState === 'passthrough-before-access-control' ||
      serviceState === 'passthrough-before-auth' ||
      serviceState === 'passthrough-before-data-handling'
    ) {
      return 'degraded';
    }

    if (serviceState === 'enabled') {
      return 'available';
    }

    if (serviceState === 'disabled') {
      return 'inactive';
    }

    return serviceState || 'available';
  }

  get eolTimestamp(): number {
    return this.support.deprecated?.eolTimestamp || 0;
  }

  // @todo there's gotta be a better way to do this
  // Matches on SurePath AI and the SDK
  get isF5WAIS(): boolean {
    const surePathServiceNames = Object.values(SurePathServiceName) as string[];
    return surePathServiceNames.includes(this.name);
  }

  get isDiscovered(): boolean {
    return this.type === 'discovered';
  }

  get meta(): PublicServiceMeta {
    return {
      id: this.id || '',
      name: this.name,
      image: this.image,
      description: this.description,
      supportLevels: this.supportLevels,
      risk: this.risk,
      riskLevel: this.riskLevel,
      isF5WAIS: this.isF5WAIS,
      status: this.status,
      isDiscovered: this.isDiscovered,
    };
  }
}
